import React from "react";
import { Routes, Route } from "react-router-dom";
import AuthGuard from "./components/authGuard/AuthGuard";
import PublicOnlyGuard from "./components/authGuard/PublicOnlyGuard";
import AcceleratorDataProvider from "./hooks/acceleratorDataProvider";
import { ROUTES } from "./helpers/constants";

// Pages
import LoginPage from "./pages/login/LoginPage";
import CourseCatalog from "./pages/course/CourseCatalog ";
import CoursePage from "./pages/course";
import DashboardPage from "./pages/dashboard";
import ExploreAll from "./pages/exploreAll";
import ScCatalog from "./pages/scNetflix/ScCatalog";
import AcceleratorPage from "./pages/accelerator";
import RecordAndTrack from "./pages/recordAndTrack";
import ContactUs from "./pages/contactUs";
import CreateAccountPage from "./pages/auth/CreateAccountPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";

// Components used as pages
import OrderStatus from "./components/orderStatus";
import GiftForm from "./components/gift/GiftForm";
import ThankYou from "./components/gift/ThankYou";
import VipCoaching from "./pages/vip-coaching";
import AccountPage from "./pages/account";

// Route access levels
type RouteAccess = "public" | "publicOnly" | "protected";

interface RouteConfig {
    path: string;
    element: React.ReactNode;
    access: RouteAccess;
    wrapper?: React.FC<{ children: React.ReactNode }>;
}

const AcceleratorWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <AcceleratorDataProvider>{children}</AcceleratorDataProvider>
);

const routeConfig: RouteConfig[] = [
    // Public-only (redirect to /courses if logged in)
    { path: ROUTES.HOME, element: <LoginPage />, access: "publicOnly" },
    { path: ROUTES.LOGIN, element: <LoginPage />, access: "publicOnly" },
    

    // Courses
    { path: ROUTES.COURSES, element: <CourseCatalog />, access: "protected" },
    { path: ROUTES.COURSE_LESSON, element: <CoursePage />, access: "protected" },
    { path: ROUTES.COURSE_DETAIL, element: <CoursePage />, access: "protected" },
    { path: ROUTES.COURSE_EXPLORE, element: <ExploreAll />, access: "protected" },

    // Dashboard
    { path: ROUTES.DASHBOARD, element: <DashboardPage />, access: "protected" },
    { path: ROUTES.DASHBOARD_EXPLORE, element: <ExploreAll />, access: "protected" },

    // Scratch Club
    { path: ROUTES.SCRATCH_CLUB, element: <ScCatalog />, access: "protected" },
    { path: ROUTES.SCRATCH_CLUB_RECORD, element: <RecordAndTrack />, access: "protected", wrapper: AcceleratorWrapper },
    { path: ROUTES.SCRATCH_CLUB_ACCELERATOR_LESSON, element: <AcceleratorPage />, access: "protected", wrapper: AcceleratorWrapper },
    { path: ROUTES.SCRATCH_CLUB_ACCELERATOR_COURSE, element: <AcceleratorPage />, access: "protected", wrapper: AcceleratorWrapper },
    { path: ROUTES.SCRATCH_CLUB_ACCELERATOR, element: <AcceleratorPage />, access: "protected", wrapper: AcceleratorWrapper },

    // Utility pages
    { path: ROUTES.ORDER_STATUS, element: <OrderStatus />, access: "protected" },
    { path: `${ROUTES.ORDER_STATUS}/`, element: <OrderStatus />, access: "protected" },
    { path: ROUTES.GIFT, element: <GiftForm />, access: "protected" },
    { path: ROUTES.THANK_YOU, element: <ThankYou />, access: "protected" },

    // Public (no guard)
    { path: ROUTES.CONTACT_US, element: <ContactUs />, access: "public" },
    { path: ROUTES.CREATE_ACCOUNT, element: <CreateAccountPage />, access: "public" },
    { path: `${ROUTES.CREATE_ACCOUNT}/`, element: <CreateAccountPage />, access: "public" },
    { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage />, access: "public" },
    { path: `${ROUTES.FORGOT_PASSWORD}/`, element: <ForgotPasswordPage />, access: "public" },
    { path: ROUTES.VIP_COACHING, element: <VipCoaching />, access: "protected" },

    // Account management routes (protected)
    { path: ROUTES.ACCOUNT, element: <AccountPage />, access: "protected" },
    { path: ROUTES.ACCOUNT_MANAGE, element: <AccountPage />, access: "protected" },
    { path: ROUTES.ACCOUNT_PASSWORD, element: <AccountPage />, access: "protected" }
];

const wrapWithAccess = (element: React.ReactNode, access: RouteAccess, wrapper?: React.FC<{ children: React.ReactNode }>) => {
    let wrapped = element;

    if (wrapper) {
        const Wrapper = wrapper;
        wrapped = <Wrapper>{wrapped}</Wrapper>;
    }

    switch (access) {
        case "protected":
            return <AuthGuard>{wrapped}</AuthGuard>;
        case "publicOnly":
            return <PublicOnlyGuard>{wrapped}</PublicOnlyGuard>;
        default:
            return wrapped;
    }
};

const AppRoutes: React.FC = () => (
    <Routes>
        {routeConfig.map(({ path, element, access, wrapper }) => (
            <Route key={path} path={path} element={wrapWithAccess(element, access, wrapper)} />
        ))}
    </Routes>
);

export default AppRoutes;
